/**
 * Session Validation Test Component
 * Use this component to manually test session validation functionality
 */
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/authService';
import SessionValidationStatus from './SessionValidationStatus';

const SessionValidationTest = () => {
  const { validateSession, isAuthenticated, user } = useAuth();
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const testSessionValidation = async () => {
    setLoading(true);
    setTestResult(null);
    
    try {
      console.log('Manual session validation test started...');
      const result = await authService.validateSession();
      console.log('Manual session validation result:', result);
      
      setTestResult({
        success: true,
        valid: result,
        message: result ? 'Session is valid' : 'Session is invalid',
        timestamp: new Date().toLocaleTimeString()
      });
    } catch (error) {
      console.error('Manual session validation error:', error);
      setTestResult({
        success: false,
        error: error.message,
        timestamp: new Date().toLocaleTimeString()
      });
    } finally {
      setLoading(false);
    }
  };

  const clearAuthData = () => {
    authService.clearAuth();
    setTestResult({
      success: true,
      message: 'Authentication data cleared',
      timestamp: new Date().toLocaleTimeString()
    });
  };

  const getStoredData = () => {
    const userData = authService.getUser();
    const sessionData = authService.getSessionData();

    console.log('Stored authentication data:', {
      token: 'stored in HttpOnly cookie (not readable from JS)',
      user: userData,
      session: sessionData
    });
    
    setTestResult({
      success: true,
      message: 'Check console for stored data',
      timestamp: new Date().toLocaleTimeString()
    });
  };

  return (
    <div>
      <SessionValidationStatus />
      
      <div style={{
        padding: '20px',
        border: '1px solid #ccc',
        borderRadius: '8px',
        margin: '20px 0',
        backgroundColor: '#f9f9f9'
      }}>
        <h3>Session Validation Test</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <p><strong>Auth Status:</strong> {isAuthenticated ? 'Authenticated' : 'Not Authenticated'}</p>
          <p><strong>User:</strong> {user ? `${user.firstName} ${user.lastName}` : 'None'}</p>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <button 
            onClick={testSessionValidation} 
            disabled={loading}
            style={{
              padding: '10px 15px',
              marginRight: '10px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Testing...' : 'Test Session Validation'}
          </button>
          
          <button 
            onClick={getStoredData}
            style={{
              padding: '10px 15px',
              marginRight: '10px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Log Stored Data
          </button>
          
          <button 
            onClick={clearAuthData}
            style={{
              padding: '10px 15px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear Auth Data
          </button>
        </div>
        
        {testResult && (
          <div style={{
            padding: '10px',
            borderRadius: '4px',
            backgroundColor: testResult.success ? '#d4edda' : '#f8d7da',
            border: `1px solid ${testResult.success ? '#c3e6cb' : '#f5c6cb'}`,
            color: testResult.success ? '#155724' : '#721c24'
          }}>
            <p><strong>Result:</strong> {testResult.message || testResult.error}</p>
            {testResult.valid !== undefined && (
              <p><strong>Session Valid:</strong> {testResult.valid ? 'Yes' : 'No'}</p>
            )}
            <p><strong>Time:</strong> {testResult.timestamp}</p>
          </div>
        )}
        
        <div style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
          <p><strong>Instructions:</strong></p>
          <ol>
            <li>Make sure you're logged in first</li>
            <li>Click "Test Session Validation" to manually test</li>
            <li>Check browser console for detailed logs</li>
            <li>Try refreshing the page to test automatic validation</li>
            <li>Use "Clear Auth Data" to simulate invalid session</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default SessionValidationTest;
